// @flow
import filter from 'lodash/fp/filter';
import map from 'lodash/fp/map';
import pipe from 'lodash/fp/pipe';
import sortBy from 'lodash/fp/sortBy';

import type { TagTree } from './tag-tree';

/**
 * Full metadata type for determining how to render a Highlight
 *
 * @memberof modules/highlight
 * @param {string} displayName What is ultimately displayed as the highlight label
 * @param {string} name regex, will match against tag values
 * @param {number} priority Determines ranking for displaying the most important highlight(s)
 * @param {string} tag This is a TagTree key
 * @param {string} [displayColor] background color of the highlight, defaults to brand
 */
export type HighlightSpec = $ReadOnly<{|
  displayName: string,
  name: string,
  priority: number,
  tag: string,
  displayColor?: string,
|}>;

export type Highlight = {
  name: string,
  color: string | void,
};

type SpecWithMatch = $ReadOnly<{|
  ...HighlightSpec,
  match: boolean,
|}>;

export const tagTreeHighlights =
  (
    highlightSpecs: $ReadOnlyArray<HighlightSpec>,
  ): ((tagTree: TagTree) => $ReadOnlyArray<Highlight>) =>
  (tagTree: TagTree): $ReadOnlyArray<Highlight> =>
    (pipe(
      map((spec: HighlightSpec): SpecWithMatch => ({
        ...spec,
        match: ((Array.isArray(tagTree[spec.tag])
          ? Boolean(
              tagTree[spec.tag].find((tagValue) =>
                new RegExp(spec.name, 'i').test(tagValue),
              ),
            )
          : false): boolean),
      })),
      filter(({ match }: SpecWithMatch): boolean => match),
      sortBy<SpecWithMatch>(({ priority }) => priority * -1),
      map<SpecWithMatch, Highlight>((spec): Highlight => ({
        name: spec.displayName,
        color: spec.displayColor,
      })),
    )(highlightSpecs): $ReadOnlyArray<Highlight>);
